import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Building2, ArrowRight, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Business lines from LFPIORPI
const BUSINESS_LINES = [
  { id: "notary_public", name: "Notaría Pública" },
  { id: "real_estate", name: "Inmobiliaria" },
  { id: "jewelry", name: "Joyería y Metales Preciosos" },
  { id: "auto_dealer", name: "Compraventa de Vehículos" },
  { id: "art_antiques", name: "Obras de Arte y Antigüedades" },
  { id: "armored_transport", name: "Traslado de Valores (Blindadora)" },
  { id: "broker", name: "Intermediación Financiera" },
  { id: "money_exchange", name: "Centros Cambiarios" },
  { id: "legal_services", name: "Servicios Legales" },
  { id: "accounting", name: "Contaduría y Auditoría" },
  { id: "trust_services", name: "Fideicomisos" },
  { id: "lottery", name: "Juegos y Sorteos" },
  { id: "credit_cards", name: "Tarjetas de Crédito/Débito" },
  { id: "other", name: "Otra Actividad Vulnerable" },
];

export default function RegisterOrganization() {
  const { checkAuth } = useAuth();
  const [form, setForm] = useState({
    name: "",
    business_line: "",
    rfc: "",
    phone: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is authenticated
    const checkAuth = async () => {
      try {
        // Safari fix: Longer delay to ensure cookie from previous request is available
        // Safari needs significantly more time than Chrome/Firefox to propagate cookies
        console.log('[RegisterOrganization] Waiting for cookies to propagate...');
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        console.log('[RegisterOrganization] Checking auth...');
        
        const res = await axios.get(`${API}/auth/me`, { 
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        console.log('[RegisterOrganization] Auth check successful:', res.data);
        
        // If user already has an organization, redirect to dashboard
        if (res.data.organization_id) {
          console.log('[RegisterOrganization] User has org, redirecting to dashboard');
          navigate("/dashboard", { replace: true });
          return;
        }
        
        console.log('[RegisterOrganization] User authenticated, showing form');
        setCheckingAuth(false);
      } catch (err) {
        console.error('[RegisterOrganization] Auth check failed:', err);
        console.error('[RegisterOrganization] Error details:', err.response?.status, err.response?.data);
        
        // Set checkingAuth to false BEFORE redirecting to avoid infinite spinner
        setCheckingAuth(false);
        
        // Small delay before redirect to ensure state update
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Not authenticated, redirect to register
        navigate("/register", { replace: true });
      }
    };
    checkAuth();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!form.name.trim()) {
      setError("El nombre de la empresa es requerido");
      return;
    }
    
    if (!form.business_line) {
      setError("Selecciona el giro de tu empresa");
      return;
    }
    
    if (!form.rfc.trim()) {
      setError("El RFC es requerido");
      return;
    }
    
    // Validate RFC format (12 or 13 characters)
    const rfcCleaned = form.rfc.trim().toUpperCase();
    if (rfcCleaned.length !== 12 && rfcCleaned.length !== 13) {
      setError("El RFC debe tener 12 caracteres (moral) o 13 caracteres (física)");
      return;
    }
    
    if (!form.phone.trim()) {
      setError("El teléfono es requerido");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register/organization`, {
        name: form.name.trim(),
        business_line: form.business_line,
        rfc: form.rfc.trim().toUpperCase(),
        phone: form.phone.trim(),
      }, { 
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      console.log('Organization created:', response.data);
      
      // Refresh user context to get updated organization_id and registration_step
      console.log('[RegisterOrganization] Refreshing auth context...');
      await checkAuth();
      
      // Small delay to ensure context updates and cookies propagate (Safari fix)
      await new Promise(resolve => setTimeout(resolve, 800));
      
      console.log('[RegisterOrganization] Navigating to dashboard');
      // NEW: Redirect directly to dashboard (skip plan selection)
      // User now gets automatic 14-day Pro trial
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error('Organization creation error:', err);
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic validation errors
        setError(detail.map(e => e.msg).join(", "));
      } else if (err.code === 'ERR_NETWORK') {
        setError("Error de conexión. Verifica tu internet.");
      } else {
        setError(detail || "Error al crear la organización. Intenta de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="register-org-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-cyan-500 rounded-full filter blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-lg">
          <img src="/notalia-logo.png" alt="Notalia" className="h-14 mb-10 brightness-0 invert" />
          
          <h1 className="text-4xl font-bold text-white mb-6 leading-tight">
            Configura tu
            <span className="text-teal-400"> organización</span>
          </h1>
          
          <p className="text-slate-300 text-lg mb-8 leading-relaxed">
            Tu organización es el espacio donde gestionarás clientes, operaciones y reportes de cumplimiento.
          </p>
          
          <div className="p-6 bg-slate-800/50 rounded-xl border border-slate-700/50">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-lg bg-teal-500/20 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-teal-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Multi-usuario</h3>
                <p className="text-slate-400 text-sm">Invita a tu equipo después</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm">
              Podrás agregar más usuarios con diferentes roles (administradores, capturistas, oficiales de cumplimiento) una vez que configures tu organización.
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-slate-950">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img src="/notalia-logo.png" alt="Notalia" className="h-10 mx-auto brightness-0 invert" />
          </div>
          
          {/* Progress indicator - 2 steps instead of 3 */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-teal-500/20 border-2 border-teal-500 flex items-center justify-center text-teal-400 text-sm font-medium">✓</div>
              <span className="text-teal-400 text-sm font-medium hidden sm:inline">Cuenta</span>
            </div>
            <div className="w-12 h-px bg-teal-500" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center text-white text-sm font-medium">2</div>
              <span className="text-teal-400 text-sm font-medium hidden sm:inline">Organización</span>
            </div>
          </div>
          
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white">
              Datos de tu empresa
            </h2>
            <p className="text-slate-400 mt-2">
              Esta información aparecerá en tus reportes oficiales
            </p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-300">
                Nombre de la empresa <span className="text-red-400">*</span>
              </Label>
              <Input
                id="name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Mi Notaría S.C."
                required
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 h-11"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="business_line" className="text-slate-300">
                Giro / Actividad vulnerable <span className="text-red-400">*</span>
              </Label>
              <select
                id="business_line"
                name="business_line"
                value={form.business_line}
                onChange={handleChange}
                required
                style={{
                  WebkitAppearance: 'menulist',
                  appearance: 'menulist',
                }}
                className="w-full h-11 px-3 bg-slate-900 border border-slate-700 rounded-md text-white focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              >
                <option value="" style={{ color: '#94a3b8' }}>Selecciona un giro...</option>
                <option value="notary_public" style={{ color: '#ffffff', backgroundColor: '#0f172a' }}>Notaría Pública</option>
                <option value="real_estate" style={{ color: '#ffffff', backgroundColor: '#0f172a' }}>Inmobiliaria</option>
                <option value="jewelry" style={{ color: '#ffffff', backgroundColor: '#0f172a' }}>Joyería y Metales Preciosos</option>
                <option value="auto_dealer" style={{ color: '#ffffff', backgroundColor: '#0f172a' }}>Compraventa de Vehículos</option>
                <option value="art_antiques" style={{ color: '#ffffff', backgroundColor: '#0f172a' }}>Obras de Arte y Antigüedades</option>
                <option value="armored_transport" style={{ color: '#ffffff', backgroundColor: '#0f172a' }}>Traslado de Valores (Blindadora)</option>
                <option value="broker" style={{ color: '#ffffff', backgroundColor: '#0f172a' }}>Intermediación Financiera</option>
                <option value="money_exchange" style={{ color: '#ffffff', backgroundColor: '#0f172a' }}>Centros Cambiarios</option>
                <option value="legal_services" style={{ color: '#ffffff', backgroundColor: '#0f172a' }}>Servicios Legales</option>
                <option value="accounting" style={{ color: '#ffffff', backgroundColor: '#0f172a' }}>Contaduría y Auditoría</option>
                <option value="trust_services" style={{ color: '#ffffff', backgroundColor: '#0f172a' }}>Fideicomisos</option>
                <option value="lottery" style={{ color: '#ffffff', backgroundColor: '#0f172a' }}>Juegos y Sorteos</option>
                <option value="credit_cards" style={{ color: '#ffffff', backgroundColor: '#0f172a' }}>Tarjetas de Crédito/Débito</option>
                <option value="other" style={{ color: '#ffffff', backgroundColor: '#0f172a' }}>Otra Actividad Vulnerable</option>
              </select>
              <p className="text-xs text-slate-500">
                Según catálogo de actividades vulnerables LFPIORPI
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="rfc" className="text-slate-300">
                RFC <span className="text-red-400">*</span>
              </Label>
              <Input
                id="rfc"
                name="rfc"
                value={form.rfc}
                onChange={handleChange}
                placeholder="ABC123456XYZ"
                maxLength={13}
                required
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 h-11 uppercase"
              />
              <p className="text-xs text-slate-500">
                12 caracteres (persona moral) o 13 (persona física)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-slate-300">
                Teléfono <span className="text-red-400">*</span>
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                placeholder="+52 55 1234 5678"
                required
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 h-11"
              />
              <p className="text-xs text-slate-500">
                Incluye código de país (ej. +52 para México)
              </p>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/register")}
                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 h-12"
              >
                <ArrowLeft size={18} className="mr-2" />
                Atrás
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-teal-600 hover:bg-teal-500 text-white font-medium h-12"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Guardando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Continuar
                    <ArrowRight size={18} />
                  </span>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
